import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { Booking, Lesson, type ProRataPreviewResponse } from '@court-sync/shared';
import { paymentService, type FeePreviewResult } from '../services/payment';
import { Card, CardContent, CardHeader } from './ui/Card';
import { Button } from '@/components/ui';
import { Input, Field, FieldLabel } from './ui';
import { Alert, AlertDescription } from './ui/Alert';
import { Loader2, CreditCard, CheckCircle, Calendar, Clock, BookOpen } from 'lucide-react';
import { STRIPE_PUBLISHABLE_KEY } from '../config/api';
import { lessonService } from '../services/lesson';
import { useAuthStore } from '../store/auth';
import { useGuardianPaths } from '../hooks/useGuardianPaths';
import { useStripeElementsAppearance } from '../hooks/useStripeElementsAppearance';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function formatLessonDays(daysOfWeek?: number[]): string | null {
  if (!daysOfWeek?.length) return null;
  return daysOfWeek.map((day) => DAY_NAMES[day] ?? '').filter(Boolean).join(', ');
}

export type LessonPaymentFormVariant = 'page' | 'modal';

interface LessonPaymentStudentContext {
  firstName: string;
  lastName: string;
}

interface LessonPaymentFormProps {
  booking: Booking;
  lesson: Lesson;
  customerEmail: string;
  onSuccess?: (payment: any) => void;
  onError?: (error: string) => void;
  onCancel?: () => void;
  variant?: LessonPaymentFormVariant;
  student?: LessonPaymentStudentContext | null;
}

function formatStudentInitials(student: LessonPaymentStudentContext): string {
  const first = student.firstName.trim().charAt(0);
  const last = student.lastName.trim().charAt(0);
  return `${first}${last}`.toUpperCase() || '?';
}

interface PaymentSummaryProps {
  lesson: Lesson;
  booking: Booking;
  formatMoney: (n: number) => string;
  effectiveAmount: number;
  feePreviewLoading: boolean;
  feePreview: FeePreviewResult | null;
  appliedCoupon: { code: string; amountOff: number } | null;
  isPerSessionCampDailyBooking: boolean;
  proRataPaymentLabel: string | null | undefined;
  compact?: boolean;
}

function PaymentSummary({
  lesson,
  booking,
  formatMoney,
  effectiveAmount,
  feePreviewLoading,
  feePreview,
  appliedCoupon,
  isPerSessionCampDailyBooking,
  proRataPaymentLabel,
  compact = false,
}: PaymentSummaryProps) {
  return (
    <div
      className={
        compact
          ? 'space-y-1 rounded-lg border border-border bg-muted/40 p-4'
          : 'space-y-3 rounded-lg border border-border bg-muted/40 p-5'
      }
    >
      {!compact && (
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Order summary
        </h3>
      )}
      <div className="space-y-2 text-sm text-foreground">
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Lesson</span>
          <span className="text-right font-medium">{lesson.name}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Time</span>
          <span className="text-right">{lesson.startTime} – {lesson.endTime}</span>
        </div>
        {isPerSessionCampDailyBooking ? (
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">{`1 camp day × ${formatMoney(lesson.campDayPrice ?? 0)}`}</span>
            <span>{formatMoney(booking.totalAmount)}</span>
          </div>
        ) : typeof proRataPaymentLabel === 'string' && proRataPaymentLabel.length > 0 ? (
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">{proRataPaymentLabel}</span>
            <span>{formatMoney(booking.totalAmount)}</span>
          </div>
        ) : (
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Lesson price</span>
            <span>{formatMoney(booking.totalAmount)}</span>
          </div>
        )}
        {appliedCoupon && (
          <div className="flex justify-between gap-4 text-green-700 dark:text-green-400">
            <span>Promo code</span>
            <span>−{formatMoney(appliedCoupon.amountOff)}</span>
          </div>
        )}
        {feePreview && feePreview.processingFee > 0 && (
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Processing fee</span>
            <span>{formatMoney(feePreview.processingFee)}</span>
          </div>
        )}
        {feePreview && feePreview.taxAmount > 0 && (
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">
              {feePreview.taxInclusive ? 'Includes tax' : 'Tax'}
            </span>
            <span>{formatMoney(feePreview.taxAmount)}</span>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between border-t border-border pt-3">
        <span className={compact ? 'text-base font-semibold text-foreground' : 'text-lg font-semibold text-foreground'}>
          Total
        </span>
        <span className={compact ? 'text-xl font-bold text-green-600 dark:text-green-500' : 'text-2xl font-bold text-green-600 dark:text-green-500'}>
          {feePreviewLoading ? '…' : formatMoney(effectiveAmount)}
        </span>
      </div>
    </div>
  );
}

/** Renders inside <Elements options={{ clientSecret }}> — confirms existing PI (matches Connect on_behalf_of). */
function LessonPaymentConfirmInner({
  name,
  customerEmail,
  booking,
  lesson,
  effectiveAmount,
  formatMoney,
  onSuccess,
  onError,
  onCancel,
  variant,
}: {
  name: string;
  customerEmail: string;
  booking: Booking;
  lesson: Lesson;
  effectiveAmount: number;
  formatMoney: (n: number) => string;
  onSuccess?: (payment: any) => void;
  onError?: (error: string) => void;
  onCancel?: () => void;
  variant: LessonPaymentFormVariant;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!name.trim()) {
      setError('Please enter your full name.');
      return;
    }

    if (!stripe || !elements) {
      setError('Stripe has not loaded yet. Please try again.');
      return;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message || 'Please check your payment details.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: paymentError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}${window.location.pathname}`,
          payment_method_data: {
            billing_details: {
              name: name.trim(),
              email: customerEmail,
            },
          },
        },
        redirect: 'if_required',
      });

      if (paymentError) {
        setError(paymentError.message || 'Payment failed');
        onError?.(paymentError.message || 'Payment failed');
      } else if (paymentIntent?.status === 'succeeded') {
        if (paymentIntent.id) {
          try {
            await paymentService.confirmLessonPayment(paymentIntent.id);
          } catch (err) {
            console.warn('Confirm payment callback failed (webhook may have run):', err);
          }
        }
        onSuccess?.({
          paymentIntent,
          booking,
          lesson,
        });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to process payment';
      setError(message);
      onError?.(message);
    } finally {
      setLoading(false);
    }
  };

  const isPage = variant === 'page';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          Payment method
        </label>
        <div className="rounded-md border border-border p-3 sm:p-4">
          <PaymentElement options={{ layout: 'tabs' }} />
        </div>
      </div>

      <div className={isPage ? 'space-y-3 pt-1' : 'flex gap-3 pt-2'}>
        {!isPage && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
            className="flex-1"
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={loading || !stripe}
          className={isPage ? 'w-full' : 'flex-1'}
          size={isPage ? 'lg' : 'default'}
          isLoading={loading}
        >
          {!loading && (
            <>
              <CreditCard className="mr-2 h-4 w-4" />
              Pay {formatMoney(effectiveAmount)}
            </>
          )}
        </Button>
      </div>

      {isPage && onCancel && (
        <div className="text-center">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline disabled:opacity-50"
          >
            Cancel and go back
          </button>
        </div>
      )}

      <p className="text-center text-xs text-muted-foreground">
        Your payment is secure and encrypted. You will receive a receipt via email.
      </p>
    </form>
  );
}

interface LessonPaymentShellProps extends LessonPaymentFormProps {
  publishableKey: string;
}

const LessonPaymentShell: React.FC<LessonPaymentShellProps> = ({
  publishableKey,
  booking,
  lesson,
  customerEmail,
  onSuccess,
  onError,
  onCancel,
  variant = 'page',
  student,
}) => {
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const { appearance, isDark: stripeDarkMode } = useStripeElementsAppearance();
  const navigate = useNavigate();
  const paths = useGuardianPaths();
  const { user } = useAuthStore();
  const [success, setSuccess] = useState(false);
  const [name, setName] = useState('');
  const [hasCouponCode, setHasCouponCode] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; amountOff: number } | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [feePreview, setFeePreview] = useState<FeePreviewResult | null>(null);
  const [feePreviewLoading, setFeePreviewLoading] = useState(true);

  const stripePromise = useMemo(() => {
    if (!publishableKey) return null;
    const connectAccountId = feePreview?.stripeConnectAccountId;
    return loadStripe(
      publishableKey,
      connectAccountId ? { stripeAccount: connectAccountId } : undefined
    );
  }, [publishableKey, feePreview?.stripeConnectAccountId]);

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [intentLoading, setIntentLoading] = useState(false);
  const [intentError, setIntentError] = useState<string | null>(null);
  /** `undefined` until public pro-rata API responds for eligible group lessons; `null` = not pro-rated. */
  const [proRataPaymentLabel, setProRataPaymentLabel] = useState<string | null | undefined>(undefined);

  const isPage = variant === 'page';
  const cardClassName = isPage ? undefined : 'mx-auto max-w-md';

  // Fetch fee preview (subtotal, processing fee, total) for display
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setFeePreviewLoading(true);
      try {
        const res = await paymentService.getFeePreview(booking.id, appliedCoupon?.code ?? undefined);
        if (!cancelled && res.data) setFeePreview(res.data);
        else if (!cancelled) setFeePreview(null);
      } catch {
        if (!cancelled) setFeePreview(null);
      } finally {
        if (!cancelled) setFeePreviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [booking.id, appliedCoupon?.code]);

  useEffect(() => {
    const needsProRataCheck =
      lesson.lessonType === 'group' &&
      lesson.pricingType !== 'per_session' &&
      lesson.academyId;
    if (!needsProRataCheck) {
      setProRataPaymentLabel(null);
      return undefined;
    }
    let cancelled = false;
    setProRataPaymentLabel(undefined);
    void lessonService
      .getProRataPreview(lesson.id, lesson.academyId)
      .then((p: ProRataPreviewResponse) => {
        if (cancelled) return;
        if (p.isProRated && p.totalSessions > 0) {
          setProRataPaymentLabel(`${p.remainingSessions} of ${p.totalSessions} sessions (pro-rated)`);
        } else {
          setProRataPaymentLabel(null);
        }
      })
      .catch(() => {
        if (!cancelled) setProRataPaymentLabel(null);
      });
    return () => {
      cancelled = true;
    };
  }, [lesson.academyId, lesson.id, lesson.lessonType, lesson.pricingType]);

  // Pre-fill user's name from auth store or booking
  useEffect(() => {
    if (!name) {
      if (user?.firstName && user?.lastName) {
        setName(`${user.firstName} ${user.lastName}`.trim());
      } else if (booking.customerFirstName && booking.customerLastName) {
        setName(`${booking.customerFirstName} ${booking.customerLastName}`.trim());
      } else if (user?.firstName) {
        setName(user.firstName);
      } else if (booking.customerFirstName) {
        setName(booking.customerFirstName);
      }
    }
  }, [user, booking, name]);

  // Create PaymentIntent on the server first, then bind Elements with clientSecret.
  useEffect(() => {
    if (feePreviewLoading) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setIntentLoading(true);
      setIntentError(null);
      setClientSecret(null);
      try {
        const paymentIntentResponse = await paymentService.createLessonPayment(booking.id, appliedCoupon?.code);
        if (cancelled) return;
        const secret = paymentIntentResponse.data?.clientSecret;
        if (secret) setClientSecret(secret);
        else setIntentError('Could not start payment. Please try again.');
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to start payment';
          setIntentError(message);
          onErrorRef.current?.(message);
        }
      } finally {
        if (!cancelled) setIntentLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [booking.id, appliedCoupon?.code, feePreviewLoading]);

  const effectiveAmount = feePreview?.totalToCharge ?? (appliedCoupon
    ? Math.max(0, Math.round((booking.totalAmount - appliedCoupon.amountOff) * 100) / 100)
    : booking.totalAmount);
  const currency = feePreview?.currency?.toUpperCase() || 'USD';
  const formatMoney = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n);

  const isPerSessionCampDailyBooking =
    lesson.lessonType === 'camp' &&
    lesson.campDayPrice != null &&
    booking.sessionDate != null;

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setApplyingCoupon(true);
    setCouponError(null);
    try {
      const res = await paymentService.validateCoupon(booking.id, couponCode.trim());
      if (res.success && res.data?.valid && res.data.amountOff != null) {
        setAppliedCoupon({ code: couponCode.trim(), amountOff: res.data.amountOff });
      } else {
        setCouponError(res.data?.error || 'Invalid or expired coupon');
        setAppliedCoupon(null);
      }
    } catch {
      setCouponError('Failed to validate coupon');
      setAppliedCoupon(null);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handlePaymentSuccess = (payload: any) => {
    setSuccess(true);
    onSuccess?.(payload);
  };

  const summaryProps: PaymentSummaryProps = {
    lesson,
    booking,
    formatMoney,
    effectiveAmount,
    feePreviewLoading,
    feePreview,
    appliedCoupon,
    isPerSessionCampDailyBooking,
    proRataPaymentLabel,
  };

  const couponSection = (
    <div className="space-y-2 rounded-lg border border-border bg-muted/40 p-4" data-testid="lesson-payment-coupon">
      <label className="flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          checked={hasCouponCode}
          onChange={(e) => {
            const checked = e.target.checked;
            setHasCouponCode(checked);
            if (!checked) {
              setCouponCode('');
              setAppliedCoupon(null);
              setCouponError(null);
            }
          }}
          className="rounded border-input text-primary focus:ring-primary"
        />
        <span className="text-sm font-medium text-foreground">
          I have a coupon code
        </span>
      </label>
      {hasCouponCode && (
        <>
          <div className="flex gap-2 pt-1">
            <Field className="flex-1">
              <FieldLabel htmlFor="lesson-coupon-code" className="sr-only">Coupon code</FieldLabel>
              <Input id="lesson-coupon-code" type="text" value={couponCode} onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(null); }} placeholder="Enter code" disabled={!!appliedCoupon} className="flex-1 uppercase" />
            </Field>
            {!appliedCoupon ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleApplyCoupon}
                disabled={applyingCoupon || !couponCode.trim()}
              >
                {applyingCoupon ? 'Checking...' : 'Apply'}
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                onClick={() => { setAppliedCoupon(null); setCouponCode(''); setCouponError(null); }}
              >
                Remove
              </Button>
            )}
          </div>
          {couponError && <p className="text-sm text-destructive">{couponError}</p>}
          {appliedCoupon && (
            <p className="text-sm text-green-600 dark:text-green-400">
              Discount: −${appliedCoupon.amountOff.toFixed(2)} applied
            </p>
          )}
        </>
      )}
    </div>
  );

  const billingSection = (
    <div className="space-y-4">
      <Field>
        <FieldLabel htmlFor="lesson-payment-name">Full name</FieldLabel>
        <Input id="lesson-payment-name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="John Doe" required />
      </Field>

      <Field>
        <FieldLabel htmlFor="lesson-payment-email">Email address</FieldLabel>
        <Input id="lesson-payment-email" type="email" value={customerEmail} disabled className="bg-muted" />
      </Field>
    </div>
  );

  const checkoutLoading = (feePreviewLoading || intentLoading) && (
    <div className="flex flex-col items-center gap-3 py-8">
      <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden />
      <p className="text-sm text-muted-foreground">Preparing secure checkout…</p>
    </div>
  );

  const stripeSection = clientSecret && stripePromise ? (
    <Elements
      key={`${clientSecret}-${stripeDarkMode ? 'dark' : 'light'}`}
      stripe={stripePromise}
      options={{ clientSecret, appearance }}
    >
      <LessonPaymentConfirmInner
        name={name}
        customerEmail={customerEmail}
        booking={booking}
        lesson={lesson}
        effectiveAmount={effectiveAmount}
        formatMoney={formatMoney}
        onSuccess={handlePaymentSuccess}
        onError={onError}
        onCancel={onCancel}
        variant={variant}
      />
    </Elements>
  ) : null;

  if (success) {
    return (
      <Card className={isPage ? 'mx-auto max-w-lg' : cardClassName}>
        <CardContent className="py-10 text-center">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-950/50">
            <CheckCircle className="h-9 w-9 text-green-600 dark:text-green-400" aria-hidden />
          </div>
          <h3 className="mb-2 text-xl font-semibold text-foreground">
            Payment successful
          </h3>
          <p className="mb-6 text-muted-foreground">
            Your payment for {lesson.name} has been processed. You&apos;re all set!
          </p>
          <div className="mb-6 rounded-lg border border-border bg-muted/40 p-4 text-left text-sm">
            <dl className="space-y-2">
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Amount paid</dt>
                <dd className="font-medium text-foreground">{formatMoney(effectiveAmount)}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Lesson</dt>
                <dd className="text-right font-medium text-foreground">{lesson.name}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Booking ID</dt>
                <dd className="font-mono text-xs text-foreground">{booking.id}</dd>
              </div>
            </dl>
          </div>
          <Button onClick={() => navigate(paths?.bookings ?? '/academy-picker')} size="lg" className="w-full sm:w-auto">
            View my bookings
          </Button>
        </CardContent>
      </Card>
    );
  }

  const scheduleDays = formatLessonDays(lesson.daysOfWeek);

  const lessonContextCard = (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className="border-b border-border bg-muted/30 px-5 py-4">
          <div className="flex items-start gap-4">
            {student ? (
              <div
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary"
                aria-hidden
              >
                {formatStudentInitials(student)}
              </div>
            ) : (
              <div
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
                aria-hidden
              >
                <BookOpen className="h-5 w-5" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              {student && (
                <p className="text-sm font-medium text-muted-foreground">Enrolling</p>
              )}
              <p className="truncate text-lg font-semibold text-foreground">
                {student ? `${student.firstName} ${student.lastName}` : lesson.name}
              </p>
              {student && (
                <p className="mt-0.5 truncate text-sm text-muted-foreground">{lesson.name}</p>
              )}
            </div>
          </div>
        </div>
        <div className="space-y-3 px-5 py-4 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="h-4 w-4 shrink-0 text-primary" aria-hidden />
            <span>{lesson.startTime} – {lesson.endTime}</span>
          </div>
          {scheduleDays && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4 shrink-0 text-primary" aria-hidden />
              <span>{scheduleDays}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (isPage) {
    return (
      <div className="grid gap-8 lg:grid-cols-5 lg:gap-10">
        <aside className="space-y-6 lg:col-span-2">
          {lessonContextCard}
          <PaymentSummary {...summaryProps} />
        </aside>

        <section className="lg:col-span-3">
          <Card>
            <CardHeader className="border-b border-border pb-4">
              <h2 className="text-lg font-semibold text-foreground">Billing &amp; payment</h2>
              <p className="text-sm text-muted-foreground">
                Enter your details and pay securely with card or other supported methods.
              </p>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              {billingSection}
              {couponSection}
              {checkoutLoading}
              {intentError && (
                <Alert variant="destructive">
                  <AlertDescription>{intentError}</AlertDescription>
                </Alert>
              )}
              {stripeSection}
            </CardContent>
          </Card>
        </section>
      </div>
    );
  }

  return (
    <Card className={cardClassName}>
      <CardHeader className="border-b border-border pb-4">
        <h3 className="text-lg font-semibold text-foreground">
          Pay for lesson
        </h3>
        <p className="text-sm text-muted-foreground">
          {lesson.name}
        </p>
      </CardHeader>
      <CardContent className="space-y-5 pt-6">
        {billingSection}
        {couponSection}
        <PaymentSummary {...summaryProps} compact />
        {checkoutLoading}
        {intentError && (
          <Alert variant="destructive">
            <AlertDescription>{intentError}</AlertDescription>
          </Alert>
        )}
        {stripeSection}
      </CardContent>
    </Card>
  );
};

export const LessonPaymentForm: React.FC<LessonPaymentFormProps> = (props) => {
  const variant = props.variant ?? 'page';
  const publishableKey = STRIPE_PUBLISHABLE_KEY;

  if (!publishableKey) {
    return (
      <Card className={variant === 'page' ? undefined : 'mx-auto max-w-md'}>
        <CardContent className="p-6">
          <Alert variant="destructive">
            <AlertDescription>
              Stripe is not configured. Please check your environment variables.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return <LessonPaymentShell {...props} variant={variant} publishableKey={publishableKey} />;
};

export default LessonPaymentForm;
